/*jshint bitwise:false */
define('bindingHandlers/lineChart',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'moment',
    'lib/d3',
    'lib/dc',
    'util/dcChartUtils',
    'extensions/i18n/knockout.i18n'
], function (
    $,
    _,
    ko,
    moment,
    d3,
    dc,
    chartUtils
) {
    'use strict';

    // ====================================================================
    // Constants & Variables
    // ====================================================================

    /* Generic chart canvas class */
    var CANVAS_CLASS = "chart-canvas";

    /* Default chart padding */
    var CHART_PADDING = 100;

    /* Right margin, needed to offset chart for legend */
    var RIGHT_MARGIN = 180;

    /* Unique DOMDATA key used for storing data on DOM element */
    var LINE_CHART_DOMDATA_KEY = "line-chart-binding-data";

    /* Padding for y-axis */
    var Y_AXIS_LABEL_PADDING = CHART_PADDING / 4;

    /* Color constant for target line */
    var TARGET_COLOR = '#FF0000';

    /* Default margins to buffer the chart object */
    var DEFAULT_MARGINS = {
        top: CHART_PADDING / 2,
        right: RIGHT_MARGIN,
        bottom: CHART_PADDING / 2,
        left: CHART_PADDING
    };

    // ====================================================================
    // Knockout Binding Handler
    // ====================================================================

    /**
     * Creates an instance of the DC.js line chart using crossfilter.
     *
     * @param {Object} data.dimension - A dimension object created by the
     *  crossfilter library. This provides a specific context used by DC
     *  to break down data passed into the crossfilter object. See
     *  crossfilter documentation for more information on dimensions:
     *  https://github.com/square/crossfilter/wiki/API-Reference#dimension
     *
     * @param {Array} data.groups - An array of groups created from a
     *  given crossfilter dimension. Each group is a subset of data for a
     *  given dimension. Each group will be plotted as an individual line
     *  chart and composed together. See crossfilter documentation on for
     *  more information on groups:
     *  https://github.com/square/crossfilter/wiki/API-Reference#group-map-reduce
     *
     * @param {Object} data.crossfilter - A crossfilter object created
     *  from a set of data. See crossfilter documentatino for more information
     *  on crossfilter:
     *  https://github.com/square/crossfilter/wiki/API-Reference
     *
     * @param {Object} [data.accessor=undefined] - The function used by the line charts to
     *  get the value of a custom group object.
     *
     * @param {Array} [seriesTitles=undefined] - An array of strings or string keys that
     *  will be used as labels for each group. Groups and titles should have
     *  a 1:1 relationship.
     *
     * @param {Array} xAxis.domain - An array of 2 values representing the
     *  range of the chart's X axis. At this time, the only support is for date
     *  values.
     *
     * @param {String} [xAxis.type=undefined] - The type of the axis. Only
     *  two types are currently supported: time and linear. If the type is
     *  not specified or set to time, a d3 time scale will be used. If any
     *  other type is specified, a linear scale will be used.
     *
     * @param {Object} [xAxis.units=dc.units.integers] - The function to determine the
     *  data points on the X axis. If the units function is not specified,
     *  dc.units.integers will be used. See dc documention for more information:
     *  https://github.com/dc-js/dc.js/blob/master/web/docs/api-latest.md
     *
     * @param {Object} [xAxis.rounding=undefined] - The function to affect brush
     *  behavior when selecting. Ex: d3.time.month.round will make the brush
     *  select only whole months.
     *
     * @param {Integer} [xAxis.ticks=undefined] - The number of tick marks that
     *  appear in the chart's X axis. If the number of ticks are not specified,
     *  the dc.js default behavior is used.
     *
     * @param {String || Object} [xAxis.tickFormat=undefined] - The desired format of the
     *  X axis values. If tickFormat is not specified, the format is determined
     *  by the X axis type.
     *
     * @param {Integer} [xAxis.innerTickSize=6] - The length of the tick lines. If
     *  not specified, d3 defaults to 6 pixels. See d3 documentation for more information:
     *  https://github.com/mbostock/d3/wiki/SVG-Axes
     *
     * @param {Integer} [xAxis.outerTickSize=6] - The length of the square ends of
     *  the domain path on the axis line. If not specified, d3 defaults to 6 pixels.
     *  See d3 documentation for more information:
     *  https://github.com/mbostock/d3/wiki/SVG-Axes
     *
     * @param {Object} [xAxis.drawMinorMajorTicks=undefined] - A custom function to
     *  allow for different lengths of inner ticks. Function should use a d3 selection to
     *  set the tick's y2 attribute to a specific integer.
     *
     * @param {Object} [xAxis.drawTickLabels=undefined] - A custom function to add
     *  secondary text elements beneath a tick's default text element.
     *
     * @param {Boolean} [xAxis.inclusive=undefined] - The default behavior for DC is to exclude
     *  the high point of a provided filter range and pass that exclusive filter into the associated
     *  crossfilter dimension object. By opting for inclusive, you include the high point in any
     *  calculations based on the dimension's crossfilter object
     *
     * @param {Boolean} [xAxis.elastic=false] - Configure elastic x axis behavior. If x axis
     *  elasticity is turned on, then the line chart will attempt to recalculate the x axis
     *  range whenever a redraw event is triggered.
     *  See dc documentation for more information
     *  https://github.com/dc-js/dc.js/blob/master/web/docs/api-latest.md#elasticyboolean

     * @param {Array} yAxis.domain - An array of 2 numeric values representing
     *  the range of the chart's Y axis. These values should be numeric as
     *  the Y scale of the lineChart binding only supports a linear scale.
     *
     * @param {String} [yAxis.type=undefined] - The type of the axis. Only
     *  two types are currently supported: time, percentage, and linear. If the type is
     *  not specified or set to time, a d3 time scale will be used. If any
     *  other type is specified, a linear scale will be used.
     *
     * @param {Object} [yAxis.target=undefined] - A dc.js group created from a
     *  given crossfilter dimension. It represents the target values for a line
     *  chart.
     *
     * @param {Integer} [yAxis.ticks=undefined] - The number of tick marks that appear
     *  in the chart's Y axis. If the number of ticks is not specified, the dc.js
     *  default behavior is used.
     *
     * @param {String} [yAxis.tickFormat=undefined] - The desired format of the
     *  Y axis values. If tickFormat is not specified, the format is determined
     *  by the Y axis type.
     *
     * @param {Integer} [yAxis.innerTickSize=6] - The length of the tick lines. If
     *  not specified, d3 defaults to 6 pixels. See d3 documentation for more information:
     *  https://github.com/mbostock/d3/wiki/SVG-Axes
     *
     * @param {Integer} [yAxis.outerTickSize=6] - The length of the square ends of
     *  the domain path on the axis line. If not specified, d3 defaults to 6 pixels.
     *  See d3 documentation for more information:
     *  https://github.com/mbostock/d3/wiki/SVG-Axes
     *
     * @param {Boolean} [yAxis.limitTicks=true] - Allows you to override the number
     *  of ticks if the max Y value is less than the number of ticks. If limitTicks is
     *  set to true, or not set, the number of ticks is set to the max Y value in the domain. If
     *  set to false, the default behavior for ticks occurs.
     *
     * @param {Boolean} [yAxis.elastic=false] - Configure elastic y axis behavior. If y axis
     *  elasticity is turned on, then the line chart will attempt to recalculate the y axis
     *  range whenever a redraw event is triggered.
     *  See dc documentation for more information
     *  https://github.com/dc-js/dc.js/blob/master/web/docs/api-latest.md#elasticyboolean
     *
     * @param {Object} [y2Axis=undefined] - An option for displaying a second Y-axis
     *  on the right side of the chart. This has all of the customization options of the regular
     *  y-axis. By default, this is not enabled.
     *
     * @param {Array} [y2Axis.groups=undefined] - An array of the indices of the groups
     *  that you want to display along the right y-axis. These indices should correspond
     *  to the array you provide in data.groups. By default this is undefined so no charts
     *  display along the y-axis.
     *
     * @param {Object} [margins=DEFAULT_MARGINS] - An object containing custom values for
     *  the margins around the chart. If margins is not specified, the DEFAULT_MARGINS
     *  are used instead.
     *
     * @param {Boolean} [brushing.enabled=false] - A value to turn brushing on for a
     *  given chart. If brushing.enabled is not specified, it is set to false.
     *
     * @param {Object} [brushing.start=undefined] - A value to set the leftmost range of
     *  a chart's brush extent.
     *
     * @param {Object} [brushing.end=undefined] - A value to set the rightmost range of
     *  a chart's brush extent.
     *
     * @param {Object} [brushing.remove=undefined] - A function to perform a specific operation
     *  in the ViewModel's scope when a chart's brush extent is removed manually.
     *
     * @param {Object} [brushing.change=undefined] - A function to perform a specific operation
     *  in the ViewModel's scope when a chart's brush extent is changed manually.
     *
     * @param {Array} [brushing.setBrush=undefined] - An array of two values used to set the start
     *  and end of the chart's brush selection. If setBrush is passed as an empty object,
     *  the current brush selection is cleared.
     *
     * @param {Boolean} [brushing.snapBrush=undefined] - Optional value to turn on brush snapping.
     *
     * @param {Object} [tooltip.valueFormats=undefined] - An arary of custom functions used by the composite chart to format
     *  the series values within the tooltip. This will override the dcChartUtils tooltip format.
     *
     * @param {Object} [tooltip.dateFormat=undefined] - A custom function that returns a moment format
     *  for the date in the tooltip header. This will override the dcChartUtils tooltip format.
     *
     * @param {Boolean} [tooltip.showGuideline=false] - Determines whether to track cursor position instead
     *  of series points and show a line in addition to the series dots while hovering over a chart.
     *
     * @param {Boolean} [tooltip.hideTooltip=false] - Determines whether to hide tooltips from rendering in
     *  the chart.
     *
     * @param {Integer} [padding=CHART_PADDING] - An integer value to override the padding
     *  around the width of the chart. If padding is not specified, the CHART_PADDING
     *  value is used instead.
     *
     * @param {Integer} [transitionSpeed=750] - An integer value to set the speed of
     *  chart transitions during draw/renders. By default, this is 750ms as per
     *  dc's specifications here:
     *  https://github.com/dc-js/dc.js/blob/master/web/docs/api-latest.md
     *
     * @param {String} [interpolation=undefined] - A string value
     *  to specify the type of interpolation that will be used. monotone is the default.
     *  Other possible interpolations can be found at :
     *  https://github.com/mbostock/d3/wiki/SVG-Shapes#line_interpolate
     *
     * @param {Boolean} [maintainRatio=false] - Determines the height of your
     *  chart when the window is resized. If set to true, the chart's height will grow
     *  proportionate to the width. By default, this is false which caps the chart's
     *  height at 300px >= height >= 200px.
     *
     * @param {Boolean} [maintainHeight=false] - Determines whether the chart's height should
     *  remain unchanged when window size changes. By default, this is false so the height
     *  will changed based on the ratio to the chart's width.
     *
     * @param {Boolean} [fillParent=false] - Determines whether the chart's height should
     *  fill its parent container when the window is resized. If set to true, this will make
     *  the chart's height match the height of its parent.
     *
     * @param {Boolean} [hideLegend=false] - Determines whether to hide the legend from rendering
     *  in the chart.
     *
     * @param {Array} [colors=chartUtils.COLORS] - An array of hexcodes to use as the colors
     *  for the different series in the charts. If undefined, the colors from chartUtils
     *  will be used by default.
     *
     * @param {Boolean} [renderHorizontalLines=true] - An option for displaying grid lines
     *  corresponding to the Y-axis. By default this is true and will display lines along
     *  the left Y-axis ticks.
     *
     * @param {Boolean} [useRightAxisLines=false] - An option for having the displayed grid
     *  lines correspond to the right Y-axis ticks instead of the left. By default this is
     *  false so grid lines will match up to the left Y-axis ticks.
     *
     * @param {Object} [titleAccessor=undefined] - An alternative to using the more complex
     *  tooltips. This will style the title text of an element when you hover over it.
     *
     * @param {Object} [afterResize=undefined] - An option for providing a function to fire
     *  after a chart resize
     *
     * Example Usage:
     *
     * <div class="chart" data-bind="lineChart: {
     *      data: {
     *          dimension: dateDimension,
     *          groups: lineGroups,
     *          crossfilter: crossfilter,
     *          accessor: lineChartAccessor
     *      },
     *      xAxis: {
     *          domain: lineChartDomainX,
     *          type: 'time',
     *          units: lineChartUnitsX,
     *          rounding: lineChartUnitsXRounding,
     *          ticks: 10,
     *          tickFormat: 't',
     *          innerTickSize: 6,
     *          outerTickSize: 0,
     *          drawMinorMajorTicks: lineChartMinorMajorTickFunction,
     *          drawTickLabels: lineChartTickLabelFunction,
     *          inclusive: true,
     *          elastic: true
     *      },
     *      yAxis: {
     *          domain: lineChartDomainY,
     *          type: 'time',
     *          ticks: 4,
     *          innerTickSize: 6,
     *          outerTickSize: 0,
     *          elastic: true
     *      },
     *      y2Axis: {
     *          domain: lineChartDomainY2,
     *          type: 'time',
     *          ticks: 4,
     *          innerTickSize: 6,
     *          outerTickSize: 0
     *      },
     *      margins: {
     *          top: 10,
     *          right: 10,
     *          bottom: 10,
     *          left: 10
     *      },
     *      brushing: {
     *          enabled: true,
     *          start: brushStart,
     *          end: brushEnd,
     *          remove: removeBrushFunction,
     *          change: changeBrushFunction,
     *          setBrush: lineChartBrushFilter,
     *          snapBrush: snapBrush
     *      },
     *      tooltip: {
     *          valueFormats: lineChartValueFormats,
     *          dateFormat: tooltipDateFormat,
     *          showGuideline: true
     *      }
     *      seriesTitles: titles,
     *      padding: 0,
     *      interpolation: needLinearInterpolation,
     *      transitionSpeed: 100,
     *      colors: chartColors
     *  }"></div>
     *
     **/
    ko.bindingHandlers.lineChart = {

        init: function (element, valueAccessor) {
            var $element     = $(element),
                bindingData  = ko.unwrap(valueAccessor()),
                chartWidth   = $element.width() - (CHART_PADDING / 2),
                chartHeight  = $element.height(),
                chartAspect  = chartHeight/chartWidth,
                lineCharts   = [],
                seriesTitles = [],
                tooltipsReferences = [];

            // Throw errors if insufficient data provided to binding

            if (!bindingData.data) {
                throw new Error("Line chart data parameter is required.");
            }

            if (!bindingData.data.dimension) {
                throw new Error("Line chart data.dimension parameter is required.");
            }

            if (!bindingData.data.groups) {
                throw new Error("Line chart data.group parameter is required.");
            }

            // Access elements of binding data

            var dateDim = ko.unwrap(bindingData.data.dimension);
            var groups  = ko.unwrap(bindingData.data.groups);
            var xAxis   = ko.unwrap(bindingData.xAxis);
            var yAxis   = ko.unwrap(bindingData.yAxis);
            var y2Axis  = ko.unwrap(bindingData.y2Axis);

            if (!dateDim) { throw new Error("Line chart date dimension was undefined");   }
            if (!groups)  { throw new Error("Line chart group parameter was undefined");  }

            // Data Options
            var interpolation   = bindingData.interpolation ? ko.unwrap(bindingData.interpolation)  : false;
            var accessor        = bindingData.data.accessor ? ko.unwrap(bindingData.data.accessor)  : false;
            var titles          = bindingData.seriesTitles  ? ko.unwrap(bindingData.seriesTitles)   : false;

            // Brushing Options
            var brushing        = bindingData.brushing  ? ko.unwrap(bindingData.brushing)   : false;
            var enableBrush     = brushing.enabled      ? ko.unwrap(brushing.enabled)       : false;
            var snapBrush       = brushing.snapBrush    ? ko.unwrap(brushing.snapBrush)     : false;
            var removeBrush     = brushing.remove       ? ko.unwrap(brushing.remove)        : false;
            var changeBrush     = brushing.change       ? ko.unwrap(brushing.change)        : false;
            var setBrush        = brushing.setBrush     ? ko.unwrap(brushing.setBrush)      : false;

            // Tooltip Options
            var tooltip         = bindingData.tooltip   ? ko.unwrap(bindingData.tooltip)    : false;
            var valueFormat     = tooltip.valueFormats  ? ko.unwrap(tooltip.valueFormats)   : false;
            var showGuideline   = tooltip.showGuideline ? ko.unwrap(tooltip.showGuideline)  : false;
            var tipDateFormat   = tooltip.dateFormat    ? ko.unwrap(tooltip.dateFormat)     : undefined;

            // Generic Options
            var hideLegend      = bindingData.hideLegend                ? ko.unwrap(bindingData.hideLegend)     : false;
            var maintainRatio   = bindingData.maintainRatio             ? ko.unwrap(bindingData.maintainRatio)  : false;
            var maintainHeight  = bindingData.maintainHeight            ? ko.unwrap(bindingData.maintainHeight) : false;
            var fillParent      = bindingData.fillParent                ? ko.unwrap(bindingData.fillParent)     : false;
            var afterResize     = bindingData.afterResize               ? ko.unwrap(bindingData.afterResize)    : undefined;
            var padding         = !_.isUndefined(bindingData.padding)   ? ko.unwrap(bindingData.padding)        : undefined;
            var target          = !_.isUndefined(bindingData.yAxis)     ? ko.unwrap(bindingData.yAxis.target)   : undefined;

            var targetGroup, limitTicks, elasticY;
            if (!_.isUndefined(bindingData.yAxis)) {
                targetGroup     = !_.isUndefined(bindingData.yAxis.targetGroup) ? ko.unwrap(bindingData.yAxis.targetGroup)  : undefined;
                limitTicks      = !_.isUndefined(bindingData.yAxis.limitTicks)  ? ko.unwrap(bindingData.yAxis.limitTicks)   : true;
                elasticY        = !_.isUndefined(bindingData.yAxis.elastic)     ? ko.unwrap(bindingData.yAxis.elastic)      : false;
            }

            var elasticX;
            if (!_.isUndefined(bindingData.xAxis)) {
                elasticX        = !_.isUndefined(bindingData.xAxis.elastic)     ? ko.unwrap(bindingData.xAxis.elastic)      : false;
            }

            var margins = DEFAULT_MARGINS;
            if (bindingData.margins) {
                margins = ko.unwrap(bindingData.margins);
            }

            var transitionSpeed = 750;
            if (!_.isUndefined(bindingData.transitionSpeed)) {
                transitionSpeed = ko.unwrap(bindingData.transitionSpeed);
            }

            var colors = chartUtils.COLORS;
            if (!_.isUndefined(bindingData.colors)) {
                colors = ko.unwrap(bindingData.colors);
            }
            var scale = d3.scale.ordinal().range(colors);

            var hideTooltip = false;
            if (!_.isUndefined(tooltip.hideTooltip)) {
                hideTooltip = ko.unwrap(tooltip.hideTooltip);
            } else if (enableBrush) {
                hideTooltip = true;
            }

            var renderHorizontalLines = true;
            if (!_.isUndefined(bindingData.renderHorizontalLines)) {
                renderHorizontalLines = ko.unwrap(bindingData.renderHorizontalLines);
            }

            var useRightAxisLines = false;
            if (!_.isUndefined(bindingData.useRightAxisLines)) {
                useRightAxisLines = ko.unwrap(bindingData.useRightAxisLines);
            }

            if (!valueFormat) {
                if (!_.isUndefined(bindingData.titleAccessor)) {
                    valueFormat = ko.unwrap(bindingData.titleAccessor);
                } else {
                    valueFormat = function (d) {
                        return d.key + ': ' + d.value;
                    };
                }
            }

                // Prepare DOM for chart
            $element.addClass("chart chart-line");

            var chartId = _.uniqueId("_line_chart_");

            var $canvasNode = $("<div/>", {
                "class": CANVAS_CLASS + chartId
            });

            $element.prepend($canvasNode);

            // Recalculate width and margins if overriding the default chart padding
            if (!_.isUndefined(padding) && padding >= 0) {
                chartWidth = $element.width() - (padding / 2);

                if (_.isUndefined(bindingData.margins)) {
                    margins = {
                        top: padding / 2,
                        right: padding,
                        bottom: padding / 2,
                        left: padding
                    };
                }
            }

            // Process chart data for rendering
            if (titles){
                seriesTitles = _.map(ko.unwrap(titles), function (t) {
                    return ko.i18n.renderString(t);
                });
            }

            // Set scales for x and y axis
            var minXTick, maxXTick, xScale, xAxisLabel;
            if (xAxis && xAxis.domain) {
                var xDomain = ko.unwrap(xAxis.domain);
                minXTick = xDomain[0];
                maxXTick = xDomain[1];
                xScale = d3.time.scale().domain([minXTick, maxXTick]);
            } else {
                minXTick = dateDim.bottom(1)[0].date;
                maxXTick = dateDim.top(1)[0].date;
                xScale = d3.time.scale();
            }

            if (xAxis && xAxis.title) {
                xAxisLabel = ko.i18n.renderString(xAxis.title);
            } else {
                xAxisLabel = "";
            }

            var minYTick, maxYTick, yScale, yAxisLabel;
            if (yAxis && yAxis.domain) {
                var yDomain = ko.unwrap(yAxis.domain);
                minYTick = yDomain[0];
                maxYTick = yDomain[1];
                yScale = d3.scale.linear().domain([minYTick, maxYTick]);
            } else {
                yScale = d3.scale.linear();
            }

            if (yAxis && yAxis.title) {
                yAxisLabel = ko.i18n.renderString(yAxis.title);
            } else {
                yAxisLabel = "";
            }

            // Set up the right Y-axis if provided
            var minY2Tick, maxY2Tick, y2Scale, y2AxisLabel;
            if (y2Axis) {
                if (y2Axis.domain) {
                    var y2Domain = ko.unwrap(y2Axis.domain);
                    minY2Tick = y2Domain[0];
                    maxY2Tick = y2Domain[1];
                    y2Scale = d3.scale.linear().domain([minY2Tick, maxY2Tick]);
                } else {
                    y2Scale = d3.scale.linear();
                }

                if (y2Axis.title) {
                    y2AxisLabel = ko.i18n.renderString(y2Axis.title);
                } else {
                    y2AxisLabel = "";
                }
            }

            // Create composite chart
            var compositeChart = dc.extendedCompositeChart($canvasNode[0]);

            var lineChart, title;

            // Create line charts for each series
            _.each(groups, function(g, index) {
                lineChart = dc.extendedLineChart(compositeChart);
                title = (seriesTitles.length > index) ? seriesTitles[index] : "";

                lineChart
                    .group(g, title)
                    .title(function() { return ""; })
                    .y(yScale);

                if (interpolation) {
                    lineChart
                        .interpolate(interpolation);
                }

                if (accessor) {
                    lineChart
                        .valueAccessor(accessor);
                }

                if (y2Axis && y2Axis.groups) {
                    var groupsForRightAxis = ko.unwrap(y2Axis.groups);

                    if (_.indexOf(groupsForRightAxis, index) >= 0) {
                        lineChart
                            .y(y2Scale)
                            .useRightYAxis(true);
                    }
                }

                lineCharts.push(lineChart);
            });

            // Process data for target line
            var targetLineGroup;
            if (!_.isUndefined(target) || !_.isUndefined(targetGroup)) {
                lineChart = dc.extendedLineChart(compositeChart);
                title = ko.i18n.renderString("lineChart.series.label.targetGoal");
                seriesTitles.push(title);
                if (targetGroup) {
                    targetLineGroup = targetGroup;
                } else if (!_.isUndefined(target)) {
                    targetLineGroup = dateDim.group().reduceSum(function () {
                        return target;
                    });
                }

                lineChart
                    .interpolate('step-after')
                    .group(targetLineGroup, title)
                    .title(function() { return ""; });
                lineCharts.push(lineChart);

                scale = chartUtils.modifyColorScaleForTarget(TARGET_COLOR, seriesTitles.length);
            }

            var isInMinutes = chartUtils.checkIsInMinutes(groups);

            function disposeTooltips(){
                _.each(tooltipsReferences, function(tt) {
                    tt.dispose();
                });

                tooltipsReferences.length = 0;
            }

            function generateTooltips(chart) {
                if(tooltipsReferences.length > 0){
                    disposeTooltips();
                }

                // Agregate dots (tooltip handles)
                chart.reorderDots();

                // Draw Tooltips
                var tip = chartUtils.createTooltip();
                var chartGranularity = chartUtils.getGranularity(moment(minXTick), moment(maxXTick));
                var scaleType = ko.unwrap(yAxis.type);

                var showTooltip, hideTooltip;

                if (showGuideline) {
                    showTooltip = _.partial(chartUtils.showLineChartTooltip, tip, chart, chartGranularity, scaleType, valueFormat, tipDateFormat, true);
                    hideTooltip = _.partial(chartUtils.hideLineChartTooltip, tip, chart, true);
                    chart.svg().call(tip);
                    chart.svg().on('mousemove', showTooltip);
                    chart.svg().on('mouseout', hideTooltip);
                } else {
                    showTooltip = _.partial(chartUtils.showLineChartTooltip, tip, chart, chartGranularity, scaleType, valueFormat, tipDateFormat, false);
                    hideTooltip = _.partial(chartUtils.hideLineChartTooltip, tip, chart, false);
                    chart.svg().call(tip);
                    chart.svg().selectAll(".dot").on('mouseover', showTooltip);
                    chart.svg().selectAll(".dot").on('mouseout', hideTooltip);
                }
                if (_.flatten(chart.svg().select(".aggregate-tooltip-container").selectAll(".dot")).length < 2) {
                    chart.svg().selectAll(".dot").attr("style","fill-opacity: 0.8; stroke-opacity: 0.8");
                }

                tooltipsReferences.push(tip);
            }

            // Set all properties on chart object

            compositeChart
                // styles
                .width(chartWidth)
                .height(chartHeight)
                .margins({
                    top: margins.top,
                    right: margins.right,
                    bottom: margins.bottom,
                    left: margins.left
                })
                .colors(scale)
                .shareColors(true)

                // axis
                .xAxisPadding("1%")
                .yAxisPadding("5%")
                .elasticX(elasticX)
                .elasticY(elasticY)

                .brushOn(enableBrush) // False: Turn off brushing, turn on hovering
                .renderHorizontalGridLines(renderHorizontalLines)
                .useRightAxisGridLines(useRightAxisLines)
                .title(valueFormat)
                .transitionDuration(transitionSpeed)

                // scales
                .x(xScale)
                .xAxisLabel(xAxisLabel)
                .yAxisLabel(yAxisLabel, Y_AXIS_LABEL_PADDING)

                // data
                .dimension(dateDim)

                // pull in children
                .compose(lineCharts)

                // adjustments made just after a redraw/rerender
                .renderlet(function () {
                    compositeChart.select(".axis.y").attr("class", "y-axis y axis");
                    compositeChart.select(".axis.x").attr("class", "x-axis x axis");
                    compositeChart.select(".axis.yr").attr("class", "yr-axis yr axis");
                });

            // Activate Tooltips - Need to have series titles to support the chartUtils tooltips
            if (titles && !hideTooltip) {
                compositeChart
                    // disable the default dc tooltips
                    .renderTitle(false)

                    // adjustments made just after a redraw/rerender
                    .on("postRender", _.debounce(function(chart) {
                        generateTooltips(chart);
                    }, 300))
                    .on("postRedraw", _.debounce(function(chart) {
                        generateTooltips(chart);
                    }, 300));
            }

            if (y2Axis) {
                compositeChart
                    .rightYAxisLabel(y2AxisLabel, Y_AXIS_LABEL_PADDING);
            }

            // Change the default RangedFilter behavior to be inclusive instead of exclusive
            if (xAxis && xAxis.inclusive) {
                dc.filters.RangedFilter = function (low, high) {
                    var range = new Array(low, high);
                    range.isFiltered = function (value) {
                        return value >= this[0] && value <= this[1];
                    };

                    return range;
                };
            }

            if (brushing && brushing.enabled) {
                compositeChart
                    // Focus all other charts on the filtered date range when the filtered
                    // event is fired.

                    .on('filtered', function (chart,filter) {
                        var charts = dc.chartRegistry.list();
                        var activeChartIds = _.map(charts, function(chart) {
                            return chart.chartID();
                        });

                        // Make sure the chart we're acting on is still registered
                        if (_.indexOf(activeChartIds, compositeChart.chartID()) > -1) {
                            for (var i = 0; i < charts.length; ++i) {
                                if (charts[i].chartID() !== compositeChart.chartID()) {
                                    if (charts[i].hasOwnProperty('focus')) {
                                        if (filter) {
                                            charts[i].focus([filter[0], filter[1]]);
                                        } else {
                                            charts[i].focus();
                                        }
                                    }
                                }
                            }
                        }
                    });

                // Track the start and end points of the brush selection for use in our viewModels
                if (brushing.start && brushing.end) {
                    compositeChart.renderlet(function () {
                        compositeChart.brush().on('brush', function() {
                            var selectedRange = compositeChart.extendBrush();
                            var rangedFilter = null;

                            var minXTick, maxXTick;
                            if (xAxis && xAxis.domain) {
                                minXTick = xAxis.domain()[0];
                                maxXTick = xAxis.domain()[1];
                            } else {
                                minXTick = dateDim.bottom(1)[0].date;
                                maxXTick = dateDim.top(1)[0].date;
                            }

                            if (!compositeChart.brushIsEmpty(selectedRange)) {
                                if (moment(brushing.start()).diff(moment(selectedRange[0])) !== 0) {
                                    brushing.start(moment(selectedRange[0]));
                                }

                                if (moment(brushing.end()).diff(moment(selectedRange[1])) !== 0) {
                                    brushing.end(moment(selectedRange[1]));
                                }

                                rangedFilter = dc.filters.RangedFilter(selectedRange[0], selectedRange[1]);
                            } else if (moment(selectedRange[0]).diff(moment(selectedRange[1])) === 0) {
                                if (moment(brushing.start()).diff(moment(minXTick)) !== 0) {
                                    brushing.start(moment(minXTick));
                                }

                                if (moment(brushing.end()).diff(moment(maxXTick)) !== 0) {
                                    brushing.end(moment(maxXTick));
                                }
                            }

                            dc.events.trigger(function () {
                                if (!rangedFilter) {
                                    compositeChart.filter(null);
                                } else {
                                    compositeChart.replaceFilter(rangedFilter);
                                }
                                compositeChart.redrawGroup();
                            }, dc.constants.EVENT_DELAY);
                        });
                    });

                    // Add custom function calls whenever the client interacts with the brush
                    if (removeBrush || changeBrush || snapBrush) {
                        compositeChart.renderlet(function () {
                            compositeChart.brush().on('brushend', function() {
                                var extent = compositeChart.extendBrush();

                                // If the brush extent is being removed, fire the provided removeBrush function
                                if (compositeChart.brushIsEmpty(extent) && removeBrush) {
                                    removeBrush();
                                }

                                // Automatically snap our brush selection to the nearest data points
                                if (snapBrush && !compositeChart.brushIsEmpty(extent)) {
                                    // Grab the data from our line chart group
                                    var children = compositeChart.children(),
                                        group = children[0].group(),
                                        data = group.top(Infinity);

                                    var points = _.map(data, function (point) {
                                        return point.key;
                                    });

                                    // If our points are dates, sort them correctly
                                    if (points[0] instanceof Date) {
                                        var date_sort_asc = function (date1, date2) {
                                            if (date1 > date2) {
                                                return 1;
                                            }
                                            if (date1 < date2) {
                                                return -1;
                                            }
                                            return 0;
                                        };

                                        points.sort(date_sort_asc);
                                    } else {
                                        points.sort();
                                    }

                                    var newLeftBound, newRightBound;

                                    newLeftBound = chartUtils.findNearestPoint(points, extent[0], false);
                                    newRightBound = chartUtils.findNearestPoint(points, extent[1], false);
                                    brushing.start(moment(newLeftBound));
                                    brushing.end(moment(newRightBound));

                                    compositeChart.filter([newLeftBound, newRightBound]);
                                }

                                // If the brush extent is changed, fire the provided changeBrush function
                                if (changeBrush) {
                                    changeBrush();
                                }
                            });
                        });
                    }
                }

                if (setBrush.start && setBrush.end) {
                    compositeChart.filter([setBrush.start, setBrush.end]);
                } else {
                    compositeChart.filter(null);
                }
            }

            // =====================
            // xAxis Formatting
            // =====================
            var currentTick = 0;
            var maxTicks;

            if (xAxis && xAxis.ticks >= 0) {
                compositeChart.xAxis().ticks(xAxis.ticks);
            }

            if (xAxis && xAxis.innerTickSize >= 0) {
                compositeChart.xAxis().innerTickSize(xAxis.innerTickSize);
            }

            if (xAxis && xAxis.outerTickSize >= 0) {
                compositeChart.xAxis().outerTickSize(xAxis.outerTickSize);
            }

            if (xAxis && xAxis.units) {
                compositeChart.xUnits(xAxis.units);
            } else {
                compositeChart.xUnits(d3.time.days);
            }

            if (xAxis && xAxis.rounding) {
                compositeChart.round(xAxis.rounding);
            } else {
                compositeChart.round(d3.time.hours.round);
            }

            compositeChart.xAxis().tickFormat(function (datum, index) {
                var format, granularity, originalDomain, tickFormat;

                var xTickCount = compositeChart.xAxis().ticks()[0];

                // If this chart has a filter, make sure we use the filtered range for the min and max values
                if (compositeChart.hasFilter()) {
                    minXTick = compositeChart.filter()[0];
                    maxXTick = compositeChart.filter()[1];
                }

                if (xAxis && xAxis.tickFormat) {
                    // If the provided format is a string, apply that format
                    if (typeof xAxis.tickFormat === 'string') {
                        format = datum instanceof Date ? ko.i18n.formatDate(datum, xAxis.tickFormat) : ko.i18n.format(datum, xAxis.tickFormat);
                    } else {
                        // Grab the original min and max X values in case they're needed
                        if (xAxis.domain) {
                            originalDomain = xAxis.domain();
                        } else {
                            originalDomain = [dateDim.bottom(1)[0].date, dateDim.top(1)[0].date];
                        }

                        // Use the provided format function to get the desired tick format
                        tickFormat = xAxis.tickFormat(datum, minXTick, maxXTick, originalDomain);

                        // If the format is empty, show nothing, otherwise apply the returned format
                        if (tickFormat !== "") {
                            format = datum instanceof Date ? ko.i18n.formatDate(datum, tickFormat) : ko.i18n.format(datum, tickFormat);
                        } else {
                            format = "";
                        }
                    }
                } else {
                    if (_.isUndefined(xAxis) || _.isUndefined(xAxis.type) || xAxis.type === "time") {
                        granularity = chartUtils.getGranularity(moment(minXTick), moment(maxXTick));
                        format = chartUtils.formatChartDate(datum, granularity);
                    } else {
                        format = datum;
                    }
                }

                if (index === 0) {
                    currentTick = 0;
                }
                var tickValue = ++currentTick;

                if(xTickCount > 24){
                    return (tickValue%8 === 1) ? format : "";
                } else if(xTickCount > 12) {
                    return (tickValue%4 === 0) ? format : "";
                } else if(xTickCount > 6) {
                    return (tickValue%2 === 0) ? format : "";
                } else {
                    return format;
                }

            });

            if (xAxis && xAxis.drawMinorMajorTicks) {
                compositeChart
                    .renderlet(function() {
                        xAxis.drawMinorMajorTicks(element);
                    });
            }

            if (xAxis && xAxis.drawTickLabels) {
                compositeChart
                    .renderlet(function() {
                        xAxis.drawTickLabels(element);
                    });
            }

            // =====================
            // yAxis Formatting
            // =====================
            if (yAxis && yAxis.ticks >= 0) {
                compositeChart.yAxis().ticks(yAxis.ticks);
            }

            if (yAxis && yAxis.innerTickSize >= 0) {
                compositeChart.yAxis().innerTickSize(yAxis.innerTickSize);
            }

            if (yAxis && yAxis.outerTickSize >= 0) {
                compositeChart.yAxis().outerTickSize(yAxis.outerTickSize);
            }

            if (yAxis && yAxis.domain && limitTicks) {
                maxTicks = Math.max(ko.unwrap(yAxis.domain)[1], 2);
                if (compositeChart.yAxis().ticks()[0] > maxTicks) {
                    compositeChart.yAxis().ticks(maxTicks);
                }
            }

            compositeChart.yAxis().tickFormat(function (datum) {
                var format, tickFormat;
                var yTickCount = compositeChart.yAxis().ticks()[0];

                // If this chart has a filter, make sure we use the filtered range for the min and max values
                if (compositeChart.hasFilter()) {
                    minYTick = compositeChart.y().domain()[0];
                    maxYTick = compositeChart.y().domain()[1];
                }

                if (yAxis && yAxis.tickFormat) {
                    // If the provided format is a string, apply that format
                    if (typeof yAxis.tickFormat === 'string') {
                        format = ko.i18n.format(datum, yAxis.tickFormat);
                    } else {
                        // Use the provided format function to get the desired tick format
                        tickFormat = yAxis.tickFormat(datum, minYTick, maxYTick, yTickCount);

                        // If the format is empty, show nothing, otherwise apply the returned format
                        if (tickFormat !== "") {
                            format = ko.i18n.format(datum, tickFormat);
                        } else {
                            format = "";
                        }
                    }
                } else {
                    if (!_.isUndefined(yAxis) && !_.isUndefined(yAxis.type)) {
                        if (ko.unwrap(yAxis.type) === 'percentage') {
                            format = ko.i18n.format(datum, "p0");
                        } else if (ko.unwrap(yAxis.type) === 'time') {
                            if (isInMinutes) {
                                format = ko.i18n.renderString({
                                    "name": "lineChart.axis.label.minutes",
                                    "data": {
                                        "value": ko.i18n.format(datum, "n0")
                                    }
                                });
                            } else {
                                format = ko.i18n.renderString({
                                    "name": "lineChart.axis.label.seconds",
                                    "data": {
                                        "value": ko.i18n.format((datum * 60), "n0")
                                    }
                                });
                            }
                        } else {
                            format = datum;
                        }
                    } else {
                        format = datum;
                    }
                }

                return format;
            });

            // ========================
            // Right Y Axis Formatting
            // ========================
            if (y2Axis && y2Axis.ticks >= 0) {
                compositeChart.rightYAxis().ticks(y2Axis.ticks);
            }

            if (y2Axis && y2Axis.innerTickSize >= 0) {
                compositeChart.rightYAxis().innerTickSize(y2Axis.innerTickSize);
            }

            if (y2Axis && y2Axis.outerTickSize >= 0) {
                compositeChart.rightYAxis().outerTickSize(y2Axis.outerTickSize);
            }

            if (y2Axis && y2Axis.domain && limitTicks) {
                maxTicks = Math.max(ko.unwrap(y2Axis.domain)[1], 2);
                if (compositeChart.rightYAxis().ticks()[0] > maxTicks) {
                    compositeChart.rightYAxis().ticks(maxTicks);
                }
            }

            compositeChart.rightYAxis().tickFormat(function (datum) {
                var format, tickFormat;
                var y2TickCount = compositeChart.rightYAxis().ticks()[0];

                // If this chart has a filter, make sure we use the filtered range for the min and max values
                if (compositeChart.hasFilter()) {
                    minY2Tick = compositeChart.rightY().domain()[0];
                    maxY2Tick = compositeChart.rightY().domain()[1];
                }

                if (yAxis && yAxis.tickFormat) {
                    // If the provided format is a string, apply that format
                    if (typeof y2Axis.tickFormat === 'string') {
                        format = ko.i18n.format(datum, y2Axis.tickFormat);
                    } else {
                        // Use the provided format function to get the desired tick format
                        tickFormat = y2Axis.tickFormat(datum, minY2Tick, maxY2Tick, y2TickCount);

                        // If the format is empty, show nothing, otherwise apply the returned format
                        if (tickFormat !== "") {
                            format = ko.i18n.format(datum, tickFormat);
                        } else {
                            format = "";
                        }
                    }
                } else {
                    if (!_.isUndefined(y2Axis) && !_.isUndefined(y2Axis.type)) {
                        if (ko.unwrap(y2Axis.type) === 'percentage') {
                            format = ko.i18n.format(datum, "p0");
                        } else if (ko.unwrap(y2Axis.type) === 'time') {
                            if (isInMinutes) {
                                format = ko.i18n.renderString({
                                    "name": "lineChart.axis.label.minutes",
                                    "data": {
                                        "value": ko.i18n.format(datum, "n0")
                                    }
                                });
                            } else {
                                format = ko.i18n.renderString({
                                    "name": "lineChart.axis.label.seconds",
                                    "data": {
                                        "value": ko.i18n.format((datum * 60), "n0")
                                    }
                                });
                            }
                        }
                    } else {
                        format = datum;
                    }
                }

                return format;
            });

            // Render the composite chart
            compositeChart.render();

            // Render the legend
            if (titles) {
                var svg = d3.select(element).select("svg");
                scale.domain(seriesTitles);

                var legendBinding = {
                    "colors": scale,
                    "titles": seriesTitles,
                    "svg": svg,
                    "width": $element.width(),
                    "groups": groups,
                    "chart": compositeChart,
                    "target": targetLineGroup
                };
                ko.bindingHandlers.chartLegend.init(element, function () {
                    return legendBinding;
                });

                if (hideLegend) {
                    svg.selectAll(".legend").each(function() {
                        $(this).hide();
                    });
                }
            }

            // Store the line chart instance to update later
            chartUtils.setDomData(LINE_CHART_DOMDATA_KEY, element, {
                "lineChart": compositeChart
            });

            // Event Handler (resizing)
            $(window).on("resize." + CANVAS_CLASS + chartId, _.debounce(function () {

                var chart = chartUtils.getDomDataValue(LINE_CHART_DOMDATA_KEY, element, "lineChart") || compositeChart;
                var width = !_.isUndefined(padding) && padding >= 0 ? $element.width() - (padding / 2) : $element.width() - (CHART_PADDING / 2);
                var height;

                if (maintainHeight) {
                    height = $element.height();
                } else if (maintainRatio) {
                    height = Math.floor(chartAspect * width);
                } else if (fillParent) {
                    height = $element.parent().height();
                } else {
                    height = _.max([_.min([chartAspect*width, 300]), 200]);
                }

                if(width > 0 && height > 0) {
                    $element.css("height",height+"px");

                    chart.resize(width, height);

                    if (titles) {
                        var svg = d3.select(element).select("svg");
                        scale.domain(seriesTitles);

                        ko.bindingHandlers.chartLegend.update(element, function () {
                            return {
                                "colors": scale,
                                "titles": seriesTitles,
                                "svg": svg,
                                "width": $element.width(),
                                "groups": groups,
                                "chart": compositeChart,
                                "target": targetLineGroup
                            };
                        });

                        if (hideLegend) {
                            svg.selectAll(".legend").each(function() {
                                $(this).hide();
                            });
                        }

                    }

                }

                if (afterResize) {
                    afterResize();
                }

            }, 300));

            // Clean up on DOM node disposal

            ko.utils.domNodeDisposal.addDisposeCallback(element, function () {
                if (!hideTooltip) {
                    disposeTooltips();
                    compositeChart.svg().on('mousemove', null);
                    compositeChart.svg().on('mouseout', null);
                    compositeChart.svg().selectAll('.dot').on('mousemove', null);
                    compositeChart.svg().selectAll('.dot').on('mouseout', null);
                }

                compositeChart.svg().selectAll('.legend').selectAll('rect').on('click', null);
                compositeChart.svg().selectAll('.legend').selectAll('text').on('click', null);
                compositeChart.on('postRender', null);
                compositeChart.on('postRedraw', null);
                compositeChart.on('filtered', null);
                compositeChart.brush().on('brush', null);
                compositeChart.brush().on('brushend', null);
                $(window).off("resize." + CANVAS_CLASS + chartId);
                ko.utils.domData.clear(element);
                dc.deregisterChart(compositeChart);
            });
        },

        update: function (element, valueAccessor) {
            // This assumes that that the chart data structure doesn't change. If datasets are added
            // removed, then a complete render would be necessary, but there doesn't seem to be a use
            // case for that scenario as of now (12/9/14).

            var bindingData     = ko.unwrap(valueAccessor());
            var compositeChart  = chartUtils.getDomDataValue(LINE_CHART_DOMDATA_KEY, element, "lineChart");

            // Unwrap crossfilter object to create dependency

            ko.unwrap(bindingData.data.crossfilter);

            // Unwrap the brushing object so chart updates automatically if observable changes externally
            if (bindingData.brushing && bindingData.brushing.setBrush) {
                var setBrush = ko.unwrap(bindingData.brushing.setBrush);
                if (setBrush.start && setBrush.end) {
                    compositeChart.filter([setBrush.start, setBrush.end]);
                } else {
                    compositeChart.filter(null);
                }
            }

            // Redraw the chart
            compositeChart.redraw();
        }
    };
});

